import { Address } from 'pages/AccountDetail/ApproveUserForm'
import { axios } from 'api'
import Cookies from 'js-cookie'
import { cookieOptions } from 'utils'

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const responseError = (
      error as { response?: { data?: { error?: unknown } } }
    ).response?.data?.error
    if (typeof responseError === 'string') return responseError
  }

  return fallback
}

export const approveUser = async (payload: {
  email: string
  companyName: string
  phoneNumber: string
  ein?: string
  salesTaxAccount?: string
  discountRates: Record<string, number>
  isTaxable: boolean
  isBillingAndShippingAddressSame: boolean
  billingAddress: Address
  shippingAddress?: Address
}) => {
  try {
    const response = await axios.post('/accounts/signup/approve', payload)

    return {
      success: true,
      message: response.data.message || 'User approved successfully.'
    }
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to approve user.')
    }
  }
}

export const rejectUser = async (id: string, reason: string) => {
  try {
    const response = await axios.post('/accounts/signup/reject', {
      id,
      reason
    })

    return {
      success: true,
      message: response.data.message || 'User rejected successfully.'
    }
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to reject user.')
    }
  }
}

export const impersonateAccount = async (targetAccountId: string) => {
  const response = await axios.post(
    '/accounts/impersonate',
    {
      targetAccountId
    },
    {
      validateStatus: () => true
    }
  )

  if (response.status !== 200) {
    throw response.data?.error || 'Failed to login as user'
  }

  const { accessToken, refreshToken, targetEmail } = response.data

  Cookies.set('accessToken', accessToken, cookieOptions)
  Cookies.set('refreshToken', refreshToken, cookieOptions)
  Cookies.set('isImpersonated', 'true', cookieOptions)
  Cookies.set('impersonatedEmail', targetEmail, cookieOptions)

  const shopUrlString = process.env.REACT_APP_SHOP_URL
  if (!shopUrlString) throw new Error('Missing REACT_APP_SHOP_URL')
  const shopUrl = new URL(shopUrlString)
  window.open(shopUrl.toString(), '_blank')
}

export const requestPasswordVerificationCode = async (
  email?: string
): Promise<boolean> => {
  const response = await axios.post('/accounts/reset-password/request', {
    email
  })

  return response.status === 200
}

export const verifyAndUpdatePassword = async (
  code: string,
  newPassword: string,
  email?: string
): Promise<boolean> => {
  const response = await axios.post('/accounts/reset-password/confirm', {
    code,
    newPassword,
    email
  })

  return response.status === 200
}

export interface DashboardStats {
  accounts: {
    total: number
    pending: number
    approved: number
  }
  orders: {
    total: string
    byStatus: Record<string, string>
    recent: Array<{
      id: string
      salesOrderId: string
      status: string
      amount: string
      createdAt: string
    }>
  }
  revenue: {
    total: string
    paid: string
    byStatus: Record<string, string>
  }
}

export const adminEditAccount = async (
  accountId: string,
  updates: {
    email?: string
    firstName?: string
    lastName?: string
    companyName?: string
    isTaxable?: boolean
    discountRates?: Record<string, number>
  }
): Promise<{ success: boolean; message: string }> => {
  const response = await axios.patch(
    `/accounts/${accountId}/admin-edit`,
    updates,
    { validateStatus: () => true }
  )
  if (response.status === 200) {
    return {
      success: true,
      message: response.data.message || 'Account updated.'
    }
  }
  return {
    success: false,
    message: response.data?.error || 'Failed to update account.'
  }
}

export const getDashboardStats = async (): Promise<DashboardStats> => {
  const response = await axios.get('/accounts/dashboard/stats')
  return response.data
}
